import { Button, Collapse, Group, Paper } from "@mantine/core";
import { IconChevronDown, IconChevronRight } from "@tabler/icons-react";
import { DateTime } from "luxon";
import { useState } from "react";
import { backendDateOrDateTimeToLuxonDateTime } from "../../../../../utils/dates/backendDateOrDateTimeToLuxonDateTime";
import type { ChangeableFieldData } from "../../../../ChangeableFieldWidget/ChangeableFieldWidget";
import { ChangeableFieldWidget } from "../../../../ChangeableFieldWidget/ChangeableFieldWidget";
import type { Price } from "../../../DeliveryOverview.types";
import { FuturePriceWarningModal } from "./FuturePriceWarningModal/FuturePriceWarningModal";
import { PriceHistoryTable } from "./PriceHistoryTable/PriceHistoryTable";
import {
  convertChangeableFieldDataToPrices,
  convertPricesToChangeableFieldData,
  getCurrentPrice,
  getEarliestDate
} from "./utils/changeableFieldUtils";

const CHANGEABLE_FIELD_WIDGET_OPTIONS = {
  addButtonText: "weitere Preisänderung hinzufügen",
  addNullStartDateWhenNoValues: true,
  endDatePlaceholder: "-",
  startDatePlaceholder: "-",
  deleteTooltipText: "Preisänderung entfernen",
  numberValueHeader: "Preis",
  saveButtonText: "Übernehmen",
  collapsible: true
};

export interface ChangeablePrice {
  id?: number;
  firstDate: string | null;
  lastDate: string | null;
  value: number;
}

//TODO: Adjust this once the usage of apiWithCamilization is used consistently // noqa: MISSING_TODO_LINK
// This is a workaround for the mixed case keys in incoming prices
type PriceWithMixedDateKeys = Omit<Price, "first_date" | "last_date"> & {
  id?: number;
  value: number;
  first_date?: string | null;
  last_date?: string | null;
  firstDate?: string | null;
  lastDate?: string | null;
};

function standardizePriceDateKeys(price: PriceWithMixedDateKeys): Price {
  const firstDate = price.first_date ?? price.firstDate;
  const lastDate = price.last_date ?? price.lastDate;

  return {
    id: price.id,
    value: price.value,
    first_date: firstDate === undefined ? null : firstDate,
    last_date: lastDate === undefined ? null : lastDate
  };
}

export interface ChangeablePriceProps {
  allowInput?: boolean;
  fieldName: string;
  unit: string;
  value: Array<PriceWithMixedDateKeys>;
  collapsible?: boolean;
  ref?: React.Ref<HTMLDivElement>;
  onInput: (name: string, value: Array<Price>) => void;
}

function ChangeablePriceField({
  allowInput,
  fieldName,
  value: pricesWithMixedDateKeys = [],
  unit,
  onInput,
  collapsible = true,
  ref
}: ChangeablePriceProps) {
  const [isPriceHistoryOpen, setIsPriceHistoryOpen] = useState(false);
  const [isEditingPrices, setIsEditingPrices] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [priceDataForModal, setPriceDataForModal] = useState<Price[]>();

  const prices = (pricesWithMixedDateKeys || []).map(standardizePriceDateKeys);

  const optionsWithUnit = {
    ...CHANGEABLE_FIELD_WIDGET_OPTIONS,
    inputGroupText: unit,
    collapsible
  };

  function handleTogglePriceHistory() {
    setIsEditingPrices(false);
    setIsPriceHistoryOpen((open) => !open);
  }

  function handleTogglePriceEdit() {
    setIsPriceHistoryOpen(false);
    setIsEditingPrices((open) => !open);
  }

  const handleSavePrices = (prices: Array<Price>) => {
    setShowWarningModal(false);
    onInput(fieldName, prices);
    setIsEditingPrices(false);
    return Promise.resolve();
  };

  const submitPrices = (
    changeableFieldData: Array<ChangeableFieldData<number | null>>
  ) => {
    if (changeableFieldData.some((value) => value.value === null)) {
      return Promise.reject("Alle Preisänderungen müssen einen Wert haben.");
    }

    const newPrices = convertChangeableFieldDataToPrices(
      changeableFieldData as Array<ChangeableFieldData<number>>
    );
    const noIdObjects = newPrices.filter((obj) => obj.id === undefined);
    const oneMonthFromNow = DateTime.now().plus({ months: 1 });

    if (noIdObjects && noIdObjects.length > 0) {
      const firstNewDate = getEarliestDate(noIdObjects);
      const dateToCheck = firstNewDate.first_date
        ? backendDateOrDateTimeToLuxonDateTime(firstNewDate.first_date)
        : firstNewDate.last_date
          ? backendDateOrDateTimeToLuxonDateTime(firstNewDate.last_date)
          : null;

      if (dateToCheck && dateToCheck < oneMonthFromNow) {
        setShowWarningModal(true);
        setPriceDataForModal(newPrices);
        return Promise.resolve();
      }
    }

    return handleSavePrices(newPrices);
  };

  const currentPrice = getCurrentPrice(prices);

  const suggestedChangeablePrices = convertPricesToChangeableFieldData(prices);

  return (
    <div className="ChangeablePriceField" ref={ref}>
      {showWarningModal && priceDataForModal && (
        <FuturePriceWarningModal
          prices={priceDataForModal}
          onCancel={() => setShowWarningModal(false)}
          onSuccess={handleSavePrices}
        />
      )}
      <Group mb="xs">
        <Button
          disabled={!optionsWithUnit.collapsible}
          flex={1}
          justify="flex-start"
          leftSection={
            isPriceHistoryOpen ? (
              <IconChevronDown size="1.4em" strokeWidth={1} />
            ) : (
              <IconChevronRight size="1.4em" strokeWidth={1} />
            )
          }
          variant="default"
          onClick={handleTogglePriceHistory}
        >
          {currentPrice?.value}
        </Button>
        <Button
          disabled={!allowInput || isEditingPrices}
          onClick={handleTogglePriceEdit}
        >
          Preisänderung hinzufügen
        </Button>
      </Group>
      <Collapse expanded={isPriceHistoryOpen}>
        {prices && <PriceHistoryTable prices={prices} unit={unit} />}
      </Collapse>
      <Collapse expanded={isEditingPrices}>
        <Paper bd="1px solid neutral.3" mt="xs" shadow="none">
          {suggestedChangeablePrices && (
            <ChangeableFieldWidget
              dataName="Preisänderungen"
              initialValues={suggestedChangeablePrices}
              options={optionsWithUnit}
              onCancel={() => setIsEditingPrices(false)}
              onDone={submitPrices}
            />
          )}
        </Paper>
      </Collapse>
    </div>
  );
}

export { ChangeablePriceField };
